import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Tag } from "antd";
import OlMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { fromLonLat, toLonLat } from "ol/proj";
import "./PlacePicker.css";
import "ol/ol.css";
import { useTranslation } from "react-i18next";
import { useGlobalToast } from "../../context/ToastProvider";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";
import { getFleetDetails, updateFleetDetails } from "../../services/fleetDetailsFirestore";
import { createHereTileSource, normalizeHereLanguage } from "../../utils/hereMaps";
import { geocodeAddress, reverseGeocode } from "../../utils/hereGeocoding";
import { createFleetLocationMarkerStyle } from "../../utils/fleetLocationMarker";

const centerMadrid = {
  lat: 40.4168,
  lng: -3.7038,
};

export default function PlacePicker({ fleetId }) {
  const [mapCenter, setMapCenter] = useState(centerMadrid);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [initialLocation, setInitialLocation] = useState(null);
  const [placeName, setPlaceName] = useState("");
  const [pendingPlace, setPendingPlace] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const mapHostRef = useRef(null);
  const mapRef = useRef(null);
  const mapTileLayerRef = useRef(null);
  const mapMarkerSourceRef = useRef(null);
  const markerFeatureRef = useRef(null);
  const mapCenterRef = useRef(centerMadrid);
  const hereApiKeyRef = useRef("");
  const mapLanguageRef = useRef("en");
  const translationRef = useRef((key, options) => options?.defaultValue || key);
  const { t, i18n } = useTranslation();
  const toast = useGlobalToast();
  const { hereApiKey } = useGlobalSettings();
  const mapLanguage = useMemo(
    () => normalizeHereLanguage(i18n?.resolvedLanguage || i18n?.language || "en"),
    [i18n?.resolvedLanguage, i18n?.language]
  );
  const fleetLocationMarkerStyle = useMemo(
    () => createFleetLocationMarkerStyle({ scale: 1 }),
    []
  );

  useEffect(() => {
    mapCenterRef.current = mapCenter;
  }, [mapCenter]);

  useEffect(() => {
    hereApiKeyRef.current = hereApiKey;
  }, [hereApiKey]);

  useEffect(() => {
    mapLanguageRef.current = mapLanguage;
  }, [mapLanguage]);

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  useEffect(() => {
    const fetchFleetDetails = async () => {
      try {
        const data = await getFleetDetails(fleetId);
        if (data?.location) {
          const location = data.location;
          const lat = Number(location.lat);
          const lng = Number(location.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          setPlaceName(location.name || "");
          setSearchValue(location.name || "");
          setInitialLocation({ lat, lng });
        } else {
          setMapCenter(centerMadrid);
        }
      } catch (error) {
        console.error("Error fetching fleet location:", error);
      }
    };

    if (fleetId) fetchFleetDetails();
  }, [fleetId]);

  useEffect(() => {
    if (!mapHostRef.current || mapRef.current) return;

    const tileLayer = new TileLayer({
      source: createHereTileSource({
        apiKey: hereApiKeyRef.current,
        language: mapLanguageRef.current,
        style: "lite.day",
        imageFormat: "png8",
        includeLanguage: true,
        disablePois: true,
      }),
      visible: true,
    });

    const markerSource = new VectorSource();
    const markerLayer = new VectorLayer({
      source: markerSource,
      declutter: true,
    });

    const map = new OlMap({
      target: mapHostRef.current,
      layers: [tileLayer, markerLayer],
      view: new View({
        center: fromLonLat([mapCenterRef.current.lng, mapCenterRef.current.lat]),
        zoom: 13,
        minZoom: 2,
        maxZoom: 22,
      }),
    });

    const onMapClick = async (event) => {
      if (!hereApiKeyRef.current) return;
      const [lng, lat] = toLonLat(event.coordinate);
      const position = { lat, lng };
      setMarkerPosition(position);
      setMapCenter(position);

      const location = await reverseGeocode(lat, lng, hereApiKeyRef.current, {
        language: mapLanguageRef.current,
      });
      const name =
        location?.address ||
        translationRef.current("placePicker.unnamedLocation", {
          defaultValue: "Unnamed location",
        });
      setPendingPlace({
        name,
        lat,
        lng,
      });
      setSearchValue(name);
    };

    map.on("singleclick", onMapClick);

    const updateMapSize = () => map.updateSize();
    updateMapSize();
    const resizeRafId = window.requestAnimationFrame(updateMapSize);
    const resizeTimeoutId = window.setTimeout(updateMapSize, 150);
    window.addEventListener("resize", updateMapSize);
    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => map.updateSize());
      resizeObserver.observe(mapHostRef.current);
    }

    mapRef.current = map;
    mapTileLayerRef.current = tileLayer;
    mapMarkerSourceRef.current = markerSource;

    return () => {
      map.un("singleclick", onMapClick);
      window.removeEventListener("resize", updateMapSize);
      window.cancelAnimationFrame(resizeRafId);
      window.clearTimeout(resizeTimeoutId);
      if (resizeObserver) resizeObserver.disconnect();
      map.setTarget(undefined);
      mapRef.current = null;
      mapTileLayerRef.current = null;
      mapMarkerSourceRef.current = null;
      markerFeatureRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    mapTileLayerRef.current?.setSource(
      createHereTileSource({
        apiKey: hereApiKey,
        language: mapLanguage,
        style: "lite.day",
        imageFormat: "png8",
        includeLanguage: true,
        disablePois: true,
      })
    );
    mapRef.current.render();
  }, [hereApiKey, mapLanguage]);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.getView().animate({
      center: fromLonLat([mapCenter.lng, mapCenter.lat]),
      duration: 180,
    });
  }, [mapCenter]);

  useEffect(() => {
    const source = mapMarkerSourceRef.current;
    if (!source) return;
    if (!markerPosition) {
      if (markerFeatureRef.current) {
        source.removeFeature(markerFeatureRef.current);
        markerFeatureRef.current = null;
      }
      return;
    }

    const point = new Point(fromLonLat([markerPosition.lng, markerPosition.lat]));
    if (!markerFeatureRef.current) {
      const feature = new Feature({ geometry: point });
      feature.setStyle(fleetLocationMarkerStyle);
      markerFeatureRef.current = feature;
      source.addFeature(feature);
      return;
    }

    markerFeatureRef.current.setGeometry(point);
  }, [markerPosition, fleetLocationMarkerStyle]);

  const handleAddressSearch = async () => {
    const query = String(searchValue || "").trim();
    if (!query || !hereApiKey) return;

    const location = await geocodeAddress(query, hereApiKey, { language: mapLanguage });
    if (!location) {
      toast({
        title: t("common.addressNotFound", { defaultValue: "Address not found." }),
        status: "warning",
      });
      return;
    }

    const nextPosition = { lat: location.lat, lng: location.lng };
    setMapCenter(nextPosition);
    setMarkerPosition(nextPosition);
    setPendingPlace({
      name: location.address || location.name || query,
      lat: location.lat,
      lng: location.lng,
    });
  };

  const handleUpdateLocation = async () => {
    if (!pendingPlace) {
      console.error("No location selected.");
      return;
    }

    const location = {
      name:
        pendingPlace.name ||
        t("placePicker.unnamedLocation", { defaultValue: "Unnamed location" }),
      lat: pendingPlace.lat,
      lng: pendingPlace.lng,
    };

    if (!fleetId) {
      console.error("Missing fleetId");
      return;
    }

    try {
      await updateFleetDetails(fleetId, { location });

      setPlaceName(location.name);
      setSearchValue(location.name);
      setInitialLocation({ lat: location.lat, lng: location.lng });
      setMarkerPosition({ lat: location.lat, lng: location.lng });
      setPendingPlace(null);

      toast({
        title: t("toast.successLocationUpdated"),
        status: "success",
      });
    } catch (error) {
      console.error("Error updating location:", error);
      toast({ error,
        title: t("toast.errorLocationUpdate"),
        status: "warning",
      });
    }
  };

  const isSameLocation =
    initialLocation &&
    pendingPlace &&
    pendingPlace.lat === initialLocation.lat &&
    pendingPlace.lng === initialLocation.lng;

  return (
    <>
      {placeName && (
        <Tag color="cyan" style={{ marginBottom: 8 }}>
          <strong style={{ fontWeight: 600 }}>{t("setup.currentLocation")}</strong> {placeName}
        </Tag>
      )}
      <div className="placepicker-wrapper">
        <Input
          type="text"
          placeholder={t("setup.search")}
          className="placepicker-input"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          onPressEnter={handleAddressSearch}
        />

        <div className="placepicker-actions">
          <Button type="default" onClick={handleAddressSearch} disabled={!hereApiKey}>
            {t("signup.searchAddress", { defaultValue: "Search" })}
          </Button>
        </div>

        <div ref={mapHostRef} className="map-container" />

        <Button
          style={{ marginTop: 12 }}
          type="primary"
          onClick={handleUpdateLocation}
          disabled={!pendingPlace || isSameLocation}
        >
          {t("setup.locationButton")}
        </Button>
      </div>
    </>
  );
}
